import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faArrowRight,
  faUserTie,
  faComments,
  faCircleCheck,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { authorizationCaseService } from '@/services/authorization-case.service';
import type { AuthorizationJourneyHop } from '@/services/authorization-case.service';
import { fmtDateTime } from '../utils';
import {
  buildRadarNodes,
  customerParticipantLabel,
  hopLabel,
  hopMovement,
  integrityMessage,
  isGenesisHop,
  type RadarNode,
} from '../journey';

/**
 * Authorization Radar — single-case request journey (Stage 3).
 *
 * Authority: **D-112** · `AUTHORIZATION_RADAR_REQUEST_JOURNEY_DESIGN.md` v1.0
 * §3, §8, §14, §16 · the Stage 2 journey endpoint.
 *
 * ── WHAT THIS DRAWS ─────────────────────────────────────────────────────────
 * Two layers, both fed by ONE request to the Stage 2 journey endpoint:
 *
 *   1. A legend of the four governed owner domains, marking which the case has
 *      actually been through and which one holds it now.
 *   2. The persisted movements, in the order they happened.
 *
 * ── WHY IT IS SPLIT THAT WAY ────────────────────────────────────────────────
 * The legend shows WHERE THE DOMAINS ARE; the trail shows WHERE THE CASE WENT.
 * Keeping them separate is what stops the radar from drawing a connection that
 * never happened: a case going OPS -> ADMIN -> OPS -> ENGINEER must never render
 * an OPS -> COMMERCIAL arrow merely because Commercial sits between them in the
 * legend. Arrows are drawn ONLY from persisted hops.
 *
 * ── WHAT IT NEVER DRAWS ─────────────────────────────────────────────────────
 * No predicted or upcoming node (D-112 §1, Rule A). No greyed-out "next step".
 * No progress percentage. No SLA, overdue, escalation or countdown (Rule K). No
 * customer node in the ownership chain (OD-R5). Nothing derived from
 * `status`/`workflowStage` combinations.
 *
 * ── ACCESSIBILITY ───────────────────────────────────────────────────────────
 * Every state is carried by text, not by colour or animation. The pulse is
 * decorative and duplicated by a visible "Waiting for …" label, it is hidden
 * from assistive technology, and it stops under `prefers-reduced-motion`. The
 * structure is ordinary DOM — a list of nodes and an ordered list of hops — so
 * nothing depends on an SVG or canvas being interpreted.
 */

interface Props {
  authorizationId: string;
}

const NODE_STATE_TEXT: Record<RadarNode['state'], string> = {
  ACTIVE: 'Current owner',
  VISITED: 'Previously held',
  UNINVOLVED: 'Not involved',
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section
      aria-labelledby="authorization-radar-heading"
      className="rounded-xl border border-border bg-card"
    >
      <div className="border-b border-border px-5 py-3.5">
        <h3 id="authorization-radar-heading" className="text-sm font-semibold text-foreground">
          Request Journey
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Where this authorization has actually been — recorded movements only.
        </p>
      </div>
      {children}
    </section>
  );
}

/** The four governed owner domains. A legend, never a sequence. */
function OwnerLegend({ nodes }: { nodes: RadarNode[] }) {
  return (
    <ul
      aria-label="Authorization owner domains"
      className="flex flex-col gap-2 sm:flex-row sm:flex-wrap"
    >
      {nodes.map((node) => {
        const active = node.state === 'ACTIVE';
        return (
          <li
            key={node.owner}
            data-testid={`radar-node-${node.owner}`}
            data-state={node.state}
            {...(active ? { 'aria-current': 'step' as const } : {})}
            className={[
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left',
              active
                ? 'border-primary bg-primary/10'
                : node.state === 'VISITED'
                  ? 'border-border bg-muted/50'
                  : 'border-dashed border-border bg-transparent',
            ].join(' ')}
          >
            {/* Decorative only. The state is stated in text below, so neither
                colour nor motion is ever the sole indicator. */}
            <span
              aria-hidden="true"
              className={[
                'h-2.5 w-2.5 shrink-0 rounded-full',
                active
                  ? 'bg-primary animate-pulse motion-reduce:animate-none'
                  : node.state === 'VISITED'
                    ? 'bg-muted-foreground/60'
                    : 'bg-transparent ring-1 ring-inset ring-border',
              ].join(' ')}
            />
            <span className="min-w-0">
              <span
                className={[
                  'block truncate text-sm font-medium',
                  active ? 'text-foreground' : 'text-muted-foreground',
                ].join(' ')}
              >
                {node.label}
              </span>
              <span className="block text-xs text-muted-foreground">
                {NODE_STATE_TEXT[node.state]}
                {node.waitingForLabel ? ` · Waiting for ${node.waitingForLabel}` : ''}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function HopRow({ hop }: { hop: AuthorizationJourneyHop }) {
  const movement = hopMovement(hop);
  const isCustomerLeg = hop.type === 'COMMUNICATION';
  const genesis = isGenesisHop(hop);

  return (
    <li
      data-testid={`radar-hop-${hop.id}`}
      data-hop-type={hop.type}
      // The persisted action/method is preserved verbatim regardless of the
      // friendlier label rendered beside it.
      data-action={hop.action ?? ''}
      data-method={hop.method ?? ''}
      data-from-owner={hop.fromOwner ?? ''}
      data-to-owner={hop.toOwner ?? ''}
      className="relative pl-7"
    >
      <span
        aria-hidden="true"
        className={[
          'absolute left-0 top-1 flex h-5 w-5 items-center justify-center rounded-full',
          isCustomerLeg ? 'bg-amber-500/15 text-amber-700' : 'bg-primary/10 text-primary',
        ].join(' ')}
      >
        <FontAwesomeIcon
          icon={
            isCustomerLeg
              ? faComments
              : hop.action === 'RETURNED_TO_OPS' || hop.action === 'RETURNED'
                ? faRotateLeft
                : hop.action === 'RELEASED_TO_ENGINEER'
                  ? faCircleCheck
                  : faUserTie
          }
          className="h-2.5 w-2.5"
        />
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-medium text-foreground">{hopLabel(hop)}</span>
        <span
          className={[
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            isCustomerLeg ? 'bg-amber-500/10 text-amber-700' : 'bg-muted text-muted-foreground',
          ].join(' ')}
        >
          {isCustomerLeg ? 'Customer interaction' : 'Ownership'}
        </span>
      </div>

      {movement && (
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {movement.fromLabel ? (
            <span>{movement.fromLabel}</span>
          ) : (
            /* Genesis: the case had no prior owner. Said plainly rather than
               filled in with a plausible-looking department. */
            <span className="italic">{genesis ? 'Case created' : 'Not previously set'}</span>
          )}
          <FontAwesomeIcon aria-hidden="true" icon={faArrowRight} className="h-2.5 w-2.5" />
          <span className="font-medium text-foreground">{movement.toLabel}</span>
          <span className="sr-only">
            {movement.kind === 'OWNERSHIP' ? 'ownership moved to' : 'now waiting for'}{' '}
            {movement.toLabel}
          </span>
        </p>
      )}

      <p className="mt-1 text-xs text-muted-foreground">
        {isCustomerLeg ? (
          <>
            With {customerParticipantLabel(hop)}
            {hop.actorName ? ` · Recorded by ${hop.actorName}` : ''}
            {hop.actorRoleName ? ` (${hop.actorRoleName})` : ''}
          </>
        ) : (
          <>
            {hop.actorName ?? 'Unknown actor'}
            {hop.actorRoleName ? ` (${hop.actorRoleName})` : ''}
          </>
        )}
        {' · '}
        <time dateTime={hop.occurredAt}>{fmtDateTime(hop.occurredAt)}</time>
      </p>

      {hop.comment && <p className="mt-1 text-xs text-foreground/80">{hop.comment}</p>}
    </li>
  );
}

export function AuthorizationRadar({ authorizationId }: Props) {
  // ONE request for the whole journey. The backend has already merged both
  // sources, ordered them by true event time, and verified the result — none of
  // that is repeated here, and there is no per-hop request.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['authorization-case-journey', authorizationId],
    queryFn: () => authorizationCaseService.getJourney(authorizationId),
    enabled: !!authorizationId,
  });

  if (isLoading) {
    return (
      <Shell>
        <div className="px-5 py-6" data-testid="radar-loading">
          <p className="sr-only" role="status">
            Loading request journey
          </p>
          <div aria-hidden="true" className="space-y-3">
            <div className="h-14 rounded-lg bg-muted skeleton-shimmer" />
            <div className="h-14 rounded-lg bg-muted skeleton-shimmer" />
          </div>
        </div>
      </Shell>
    );
  }

  // An error is an error. It is never flattened into "no journey", which would
  // tell the user the case has no recorded history when in fact nothing is known.
  if (isError || !data) {
    return (
      <Shell>
        <div className="px-5 py-6" data-testid="radar-error">
          <p role="alert" className="text-sm font-medium text-destructive">
            The request journey could not be loaded.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {error instanceof Error && error.message
              ? error.message
              : 'Please retry, or contact support if this persists.'}
          </p>
        </div>
      </Shell>
    );
  }

  const { integrity } = data;

  // ── Design §16 (Rule H) — self-verification failed ────────────────────────
  // Show the authoritative live state, say plainly that the record does not
  // reconcile, and stop. No hops, no legend "visited" states, no repair, no
  // guessed destination. A gap in the record must be visible as a gap.
  if (!integrity.consistent) {
    return (
      <Shell>
        <div className="px-5 py-5" data-testid="radar-integrity-gap">
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
            <FontAwesomeIcon
              aria-hidden="true"
              icon={faTriangleExclamation}
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
            />
            <div>
              <p role="alert" className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Journey data could not be verified
              </p>
              <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
                {integrityMessage(data)} The authorization’s current state below remains
                authoritative. Please raise this for investigation.
              </p>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Current owner</dt>
              <dd className="text-sm font-medium text-foreground">{integrity.liveOwner}</dd>
            </div>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Waiting for</dt>
              <dd className="text-sm font-medium text-foreground">{integrity.liveWaitingFor}</dd>
            </div>
          </dl>

          <p className="mt-3 text-xs text-muted-foreground">
            Verification code: <span className="font-mono">{integrity.integrity}</span>
          </p>
        </div>
      </Shell>
    );
  }

  const nodes = buildRadarNodes(data.currentOwner, data.waitingFor, data.hops);

  return (
    <Shell>
      <div className="space-y-5 px-5 py-4">
        <OwnerLegend nodes={nodes} />

        {data.hops.length === 0 ? (
          // Verified, and genuinely empty. Distinct from both the error state
          // above and from a padded skeleton chain, which §14 forbids.
          <p data-testid="radar-empty" className="text-sm text-muted-foreground">
            No movements have been recorded for this authorization yet.
          </p>
        ) : (
          <ol
            aria-label="Recorded journey movements"
            data-testid="radar-hops"
            className="space-y-4 border-t border-border pt-4"
          >
            {data.hops.map((hop) => (
              <HopRow key={hop.id} hop={hop} />
            ))}
          </ol>
        )}
      </div>
    </Shell>
  );
}
